import { zodResolver } from "@hookform/resolvers/zod";
import { ApiError, fieldErrorsOf } from "@repo/api-client";
import { Button, IconButton } from "@repo/components";
import { useFlag } from "@repo/flags";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch, type Resolver } from "react-hook-form";
import { PageScaffold } from "../../shell/PageScaffold";
import { useVenue } from "../../shell/VenueContext";
import { MediaPicker, assetThumb, useMediaLibrary } from "../media/MediaPicker";
import { CustomDomainPanel } from "./CustomDomainPanel";
import { ListingAuthPanel } from "./ListingAuthPanel";
import { sitePublicHost, sitePublicUrl } from "./publicUrl";
import { siteApi, siteKeys, type CtaState, type SiteResponse } from "./siteApi";
import {
  buildDraftRequest,
  contrastWarning,
  formFromSite,
  isHexColor,
  moveInList,
  siteFormSchema,
  type SiteFormValues,
} from "./siteRules";

const row = css({ display: "flex", flexDirection: "column", gap: "1", marginBottom: "3" });
const section = css({
  border: "1px solid",
  borderColor: "border.default",
  borderRadius: "md",
  padding: "4",
  marginBottom: "4",
});
// Use the design-system SEMANTIC tokens (like the rest of the app) rather than hand-authored
// Tailwind-style scale steps — `fg.error` / `fg.warning` are the sanctioned form-feedback slots.
const errText = css({ color: "fg.error", fontSize: "sm" });
const warnText = css({ color: "fg.warning", fontSize: "sm" });
const actions = css({ display: "flex", gap: "2", flexWrap: "wrap", alignItems: "center" });

/**
 * The `/v/:venueId/settings/site` editor (features/public-venue-site.md, §C.11). Managers author the
 * DRAFT — description, cuisines, price range, structured/document menu, gallery order (cover first),
 * branding, contact, socials — then PUBLISH (draft→published, emits SitePublished). The server is
 * authoritative: a 409 offers reload-current (CAS), a 422 maps its `fieldErrors` back (incl. the SM-1
 * non-LIVE/other-venue asset rejection). The brand-colour contrast guard is a NON-blocking warning
 * (RGAA posture). Queries key under `["venue", venueId, "site"]` so `site.*` events invalidate them.
 */
export function SiteEditor() {
  const { venueId, venue } = useVenue();
  const { t } = useTranslation();
  const qc = useQueryClient();
  // Dark-launch gate for custom-domain CONNECT — serving (Phase B) is still dark, so connecting stays
  // gated on the same PostHog flag key the backend checks. Entitlement gating remains inside the panel.
  const customDomainConnectEnabled = useFlag("custom_domain.connect_enabled", false);
  const [notice, setNotice] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  // The venue's free public address (`<slug>.<base-domain>`), derived from the app origin — the same
  // platform-naming convention the embed widget uses. Always shown so a manager can find/share the link.
  const publicUrl = useMemo(() => sitePublicUrl(venue.slug), [venue.slug]);
  const publicHost = useMemo(() => sitePublicHost(venue.slug), [venue.slug]);
  async function copyAddress() {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (insecure context / denied) — the link is still visible to copy by hand.
    }
  }

  const siteQuery = useQuery({
    queryKey: siteKeys.site(venueId),
    queryFn: () => siteApi.get(venueId),
  });
  const site = siteQuery.data;

  const schema = useMemo(() => siteFormSchema(t), [t]);
  const form = useForm<SiteFormValues>({
    resolver: zodResolver(schema) as Resolver<SiteFormValues>,
    defaultValues: EMPTY_FORM,
    mode: "onSubmit",
  });
  const sections = useFieldArray({ control: form.control, name: "sections" });
  const socialLinks = useFieldArray({ control: form.control, name: "socialLinks" });

  useEffect(() => {
    if (site) form.reset(formFromSite(site));
  }, [site, form]);

  // `useWatch` (not `form.watch()`) — the RHF-recommended, React-Compiler-friendly way to subscribe to a
  // field; `.watch()` returns a fresh, unmemoizable function each call (react(incompatible-library)).
  const gallery = useWatch({ control: form.control, name: "galleryOrder" });
  const menuMode = useWatch({ control: form.control, name: "menuMode" });
  const primaryColor = useWatch({ control: form.control, name: "primaryColor" });
  const logoAssetId = useWatch({ control: form.control, name: "logoAssetId" });

  // Media library (deduped with the picker's own fetch) so we can render thumbnails for the stored
  // logo/gallery asset ids and offer a picker instead of raw UUID inputs (UX review P1).
  const library = useMediaLibrary(venueId);
  const assetById = useMemo(
    () => new Map((library.data ?? []).map((a) => [a.id, a])),
    [library.data],
  );
  const [picker, setPicker] = useState<"logo" | "gallery" | null>(null);
  const lowContrast = contrastWarning(primaryColor);

  async function refresh(next: SiteResponse) {
    qc.setQueryData(siteKeys.site(venueId), next);
    form.reset(formFromSite(next));
    setConflict(false);
  }

  const saveDraft = form.handleSubmit(async (values) => {
    if (!site) return;
    setBusy(true);
    setNotice(null);
    try {
      const next = await siteApi.saveDraft(venueId, buildDraftRequest(values, site.version));
      await refresh(next);
      setNotice(t("site.notice.saved"));
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  });

  function handleError(err: unknown) {
    if (err instanceof ApiError && err.status === 409) {
      setConflict(true);
      return;
    }
    const fields = fieldErrorsOf(err);
    const entries = Object.entries(fields);
    if (entries.length > 0) {
      for (const [field, message] of entries) {
        const key = mapField(field);
        if (key) form.setError(key, { type: "server", message });
      }
      setNotice(entries.map(([, m]) => m).join(" "));
      return;
    }
    setNotice(t("site.notice.saveFailed"));
  }

  async function doPublish() {
    if (!site) return;
    setBusy(true);
    setNotice(null);
    try {
      const next = await siteApi.publish(venueId, site.version);
      await refresh(next);
      setNotice(t("site.notice.published"));
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  }

  async function doPreview() {
    // Open the tab synchronously INSIDE the click gesture, then redirect it once the (async) preview URL
    // resolves. Calling window.open AFTER the await loses user-activation and the browser blocks the popup.
    const win = window.open("", "_blank");
    setBusy(true);
    try {
      const { previewUrl: url } = await siteApi.preview(venueId);
      if (win) {
        win.opener = null;
        win.location.replace(url);
      } else {
        window.location.assign(url);
      }
    } catch {
      win?.close();
      setNotice(t("site.notice.previewFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function setCta(state: CtaState, pausedUntil?: string | null) {
    if (!site) return;
    setBusy(true);
    try {
      const next = await siteApi.setBookingCta(venueId, {
        state,
        pausedUntil,
        version: site.version,
      });
      await refresh(next);
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  }

  async function toggleOffline(offline: boolean) {
    if (!site) return;
    if (offline && !window.confirm(t("site.offline.confirm"))) return;
    setBusy(true);
    try {
      const next = await siteApi.setOffline(venueId, offline, site.version);
      await refresh(next);
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  }

  async function toggleSeoIndexable(seoIndexable: boolean) {
    if (!site) return;
    if (!seoIndexable && !window.confirm(t("site.seo.hideConfirm"))) return;
    setBusy(true);
    try {
      const next = await siteApi.setSeoIndexable(venueId, seoIndexable, site.version);
      await refresh(next);
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  }

  const err = (name: keyof SiteFormValues) =>
    form.formState.errors[name]?.message as string | undefined;

  if (siteQuery.isLoading)
    return <PageScaffold title={t("site.title")}>{t("site.loading")}</PageScaffold>;
  if (!site) return <PageScaffold title={t("site.title")}>{t("site.notFound")}</PageScaffold>;

  return (
    <PageScaffold
      title={t("site.title")}
      actions={
        <div className={actions}>
          {site.hasUnpublishedChanges && (
            <span data-testid="unpublished-indicator">{t("site.unpublished")}</span>
          )}
          <Button type="button" size="sm" variant="outline" onClick={doPreview} disabled={busy}>
            {t("site.preview")}
          </Button>
          <Button type="button" size="sm" onClick={doPublish} disabled={busy}>
            {t("site.publish")}
          </Button>
        </div>
      }
    >
      {conflict && (
        <div role="alert" className={warnText}>
          {t("site.conflict")}{" "}
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={() => void siteQuery.refetch()}
          >
            {t("site.reload")}
          </Button>
        </div>
      )}
      {notice && <output>{notice}</output>}

      <fieldset className={section}>
        <legend>{t("site.address.title")}</legend>
        <div className={css({ display: "flex", alignItems: "center", gap: "2", flexWrap: "wrap" })}>
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={css({ fontFamily: "mono", wordBreak: "break-all" })}
          >
            {publicHost}
          </a>
          <Button type="button" size="xs" variant="outline" onClick={() => void copyAddress()}>
            {copied ? t("site.address.copied") : t("site.address.copy")}
          </Button>
        </div>
        <p className={css({ fontSize: "sm", color: "fg.muted", marginTop: "2" })}>
          {site.published === null
            ? t("site.address.draftOnly")
            : site.offline
              ? t("site.address.offline")
              : t("site.address.live")}
        </p>
      </fieldset>

      <form onSubmit={saveDraft} noValidate>
        <fieldset className={section}>
          <legend>{t("site.section.about")}</legend>
          <label className={row}>
            {t("site.field.description")}
            <textarea {...form.register("description")} rows={4} />
          </label>
          <CuisineTags form={form} t={t} />
          <label className={row}>
            {t("site.field.priceRange")}
            <select {...form.register("priceRange", { valueAsNumber: true })}>
              <option value={0}>{t("site.priceRange.none")}</option>
              <option value={1}>€</option>
              <option value={2}>€€</option>
              <option value={3}>€€€</option>
              <option value={4}>€€€€</option>
            </select>
          </label>
        </fieldset>

        <fieldset className={section}>
          <legend>{t("site.section.branding")}</legend>
          <div className={row}>
            <label htmlFor="site-primary-color">{t("site.field.primaryColor")}</label>
            <div className={css({ display: "flex", alignItems: "center", gap: "2" })}>
              {/* A native swatch so a restaurateur picks a colour visually; the hex box stays for a precise
                  brand value (paste #RRGGBB). Both edit the same registered field, kept in sync via watch. */}
              <input
                type="color"
                aria-label={t("site.field.primaryColor")}
                value={isHexColor(primaryColor) ? primaryColor : "#000000"}
                onChange={(e) =>
                  form.setValue("primaryColor", e.target.value, { shouldValidate: true })
                }
                className={css({
                  width: "10",
                  height: "10",
                  p: "0",
                  borderWidth: "1px",
                  borderColor: "border.default",
                  rounded: "md",
                  cursor: "pointer",
                })}
              />
              <input
                id="site-primary-color"
                {...form.register("primaryColor")}
                placeholder="#RRGGBB"
                aria-describedby="contrast-hint"
              />
            </div>
          </div>
          {err("primaryColor") && <p className={errText}>{err("primaryColor")}</p>}
          {lowContrast && (
            <output id="contrast-hint" className={warnText}>
              {t("site.contrastWarning")}
            </output>
          )}
          <div className={row}>
            <span>{t("site.field.logo")}</span>
            <div className={css({ display: "flex", alignItems: "center", gap: "3" })}>
              {logoAssetId && assetThumb(assetById.get(logoAssetId)) && (
                <img
                  src={assetThumb(assetById.get(logoAssetId)) ?? ""}
                  alt=""
                  className={css({
                    width: "12",
                    height: "12",
                    objectFit: "contain",
                    borderWidth: "1px",
                    borderColor: "border.default",
                    rounded: "md",
                  })}
                />
              )}
              <Button type="button" size="sm" variant="outline" onClick={() => setPicker("logo")}>
                {logoAssetId ? t("site.logo.change") : t("site.logo.choose")}
              </Button>
              {logoAssetId && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => form.setValue("logoAssetId", "")}
                >
                  {t("site.logo.remove")}
                </Button>
              )}
            </div>
          </div>
        </fieldset>

        <fieldset className={section}>
          <legend>{t("site.section.menu")}</legend>
          <label className={row}>
            {t("site.field.menuMode")}
            <select {...form.register("menuMode")}>
              <option value="NONE">{t("site.menuMode.none")}</option>
              <option value="STRUCTURED">{t("site.menuMode.structured")}</option>
              <option value="DOCUMENTS">{t("site.menuMode.documents")}</option>
            </select>
          </label>

          {menuMode === "DOCUMENTS" && (
            <label className={row}>
              {t("site.field.menuDocuments")}
              <input
                {...form.register("menuDocumentIds")}
                placeholder={t("site.field.assetIdsHint")}
              />
            </label>
          )}

          {menuMode === "STRUCTURED" && (
            <div>
              {sections.fields.map((sec, si) => (
                <div key={sec.id} className={section}>
                  <label className={row}>
                    {t("site.field.sectionName")}
                    <input {...form.register(`sections.${si}.name` as const)} />
                  </label>
                  <StructuredItems form={form} sectionIndex={si} t={t} />
                  <Button
                    type="button"
                    size="xs"
                    variant="ghost"
                    onClick={() => sections.remove(si)}
                  >
                    {t("site.menu.removeSection")}
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => sections.append({ name: "", items: [] })}
              >
                {t("site.menu.addSection")}
              </Button>
            </div>
          )}
        </fieldset>

        <fieldset className={section}>
          <legend>{t("site.section.gallery")}</legend>
          <p>{t("site.gallery.coverHint")}</p>
          <ol>
            {gallery.map((assetId, i) => (
              <li key={assetId} className={actions}>
                <span className={css({ display: "flex", alignItems: "center", gap: "2" })}>
                  {assetThumb(assetById.get(assetId)) && (
                    <img
                      src={assetThumb(assetById.get(assetId)) ?? ""}
                      alt={assetById.get(assetId)?.altText ?? ""}
                      className={css({
                        width: "10",
                        height: "10",
                        objectFit: "cover",
                        rounded: "sm",
                      })}
                    />
                  )}
                  {i === 0 && (
                    <span className={css({ fontSize: "xs", color: "fg.muted" })}>
                      {t("site.gallery.cover")}
                    </span>
                  )}
                </span>
                <IconButton
                  type="button"
                  size="xs"
                  variant="ghost"
                  aria-label={t("site.gallery.moveUp")}
                  disabled={i === 0}
                  onClick={() => form.setValue("galleryOrder", moveInList(gallery, i, -1))}
                >
                  ↑
                </IconButton>
                <IconButton
                  type="button"
                  size="xs"
                  variant="ghost"
                  aria-label={t("site.gallery.moveDown")}
                  disabled={i === gallery.length - 1}
                  onClick={() => form.setValue("galleryOrder", moveInList(gallery, i, 1))}
                >
                  ↓
                </IconButton>
                <IconButton
                  type="button"
                  size="xs"
                  variant="ghost"
                  aria-label={t("site.gallery.remove")}
                  onClick={() =>
                    form.setValue(
                      "galleryOrder",
                      gallery.filter((_, j) => j !== i),
                    )
                  }
                >
                  ✕
                </IconButton>
              </li>
            ))}
          </ol>
          <Button type="button" size="sm" variant="outline" onClick={() => setPicker("gallery")}>
            {t("site.gallery.add")}
          </Button>
        </fieldset>

        <fieldset className={section}>
          <legend>{t("site.section.contact")}</legend>
          <label className={row}>
            {t("site.field.phone")}
            <input {...form.register("contactPhone")} placeholder="06 12 34 56 78" />
          </label>
          <label className={row}>
            {t("site.field.email")}
            <input {...form.register("contactEmail")} type="email" />
          </label>
          <label className={row}>
            {t("site.field.website")}
            <input {...form.register("contactWebsite")} placeholder="https://" />
          </label>
          {err("contactWebsite") && <p className={errText}>{err("contactWebsite")}</p>}
        </fieldset>

        <fieldset className={section}>
          <legend>{t("site.section.social")}</legend>
          {socialLinks.fields.map((link, i) => (
            <div key={link.id} className={actions}>
              <input
                {...form.register(`socialLinks.${i}.platform` as const)}
                placeholder={t("site.field.platform")}
              />
              <input {...form.register(`socialLinks.${i}.url` as const)} placeholder="https://" />
              <Button type="button" size="xs" variant="ghost" onClick={() => socialLinks.remove(i)}>
                {t("site.social.remove")}
              </Button>
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => socialLinks.append({ platform: "INSTAGRAM", url: "" })}
          >
            {t("site.social.add")}
          </Button>
        </fieldset>

        <div className={actions}>
          <Button type="submit" disabled={busy}>
            {t("site.saveDraft")}
          </Button>
        </div>
      </form>

      <fieldset className={section}>
        <legend>{t("site.section.cta")}</legend>
        <p>
          {t("site.cta.current")}:{" "}
          <strong>{t(`site.cta.resolved.${site.bookingCta.resolved}`)}</strong>
        </p>
        <div className={actions}>
          <Button type="button" size="sm" onClick={() => void setCta("ENABLED")} disabled={busy}>
            {t("site.cta.enable")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void setCta("DISABLED_INDEFINITELY")}
            disabled={busy}
          >
            {t("site.cta.disable")}
          </Button>
          <PauseControl
            onPause={(iso) => void setCta("PAUSED_UNTIL", iso)}
            label={t("site.cta.pauseUntil")}
            submit={t("site.cta.pause")}
            disabled={busy}
          />
        </div>
      </fieldset>

      <fieldset className={section}>
        <legend>{t("site.section.offline")}</legend>
        <p>{site.offline ? t("site.offline.isOffline") : t("site.offline.isOnline")}</p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => void toggleOffline(!site.offline)}
          disabled={busy}
        >
          {site.offline ? t("site.offline.bringOnline") : t("site.offline.takeOffline")}
        </Button>
      </fieldset>

      <fieldset className={section}>
        <legend>{t("site.seo.title")}</legend>
        <p>{site.seoIndexable ? t("site.seo.visible") : t("site.seo.hidden")}</p>
        <label className={actions}>
          <input
            type="checkbox"
            checked={!site.seoIndexable}
            disabled={busy}
            onChange={(e) => void toggleSeoIndexable(!e.target.checked)}
          />
          {t("site.seo.hideToggle")}
        </label>
        <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("site.seo.hint")}</p>
      </fieldset>

      {/* Partner-listing consent (référencement) — its durable home, beside the site's own SEO visibility. */}
      <ListingAuthPanel venueId={venueId} />

      {customDomainConnectEnabled && <CustomDomainPanel venueId={venueId} />}

      {picker && (
        <MediaPicker
          venueId={venueId}
          excludeIds={picker === "gallery" ? gallery : []}
          onSelect={(id) => {
            if (picker === "logo") form.setValue("logoAssetId", id);
            else form.setValue("galleryOrder", [...gallery, id]);
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </PageScaffold>
  );
}

const chip = css({
  display: "inline-flex",
  alignItems: "center",
  gap: "1",
  px: "2",
  py: "0.5",
  fontSize: "sm",
  borderWidth: "1px",
  borderColor: "border.default",
  rounded: "full",
  bg: "bg.subtle",
});

/**
 * Cuisine types as removable chips instead of a raw comma-separated text box. The form field stays a
 * CSV string (buildDraftRequest splits on `,`), so this is a pure UI change: parse the string into tags,
 * write it back joined. Enter or a comma commits the draft; blur commits too so a half-typed tag isn't lost.
 */
function CuisineTags({
  form,
  t,
}: {
  form: ReturnType<typeof useForm<SiteFormValues>>;
  t: (k: string) => string;
}) {
  const csv = form.watch("cuisineTypes");
  const tags = useMemo(
    () =>
      csv
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [csv],
  );
  const [draft, setDraft] = useState("");
  const commit = (next: string[]) =>
    form.setValue("cuisineTypes", next.join(", "), { shouldDirty: true });
  const add = () => {
    const value = draft.trim();
    if (value && !tags.includes(value)) commit([...tags, value]);
    setDraft("");
  };
  return (
    <div className={row}>
      <span>{t("site.field.cuisineTypes")}</span>
      <div className={css({ display: "flex", flexWrap: "wrap", gap: "2", alignItems: "center" })}>
        {tags.map((tag, i) => (
          <span key={tag} className={chip}>
            {tag}
            <IconButton
              type="button"
              size="2xs"
              variant="ghost"
              aria-label={t("site.cuisine.remove")}
              onClick={() => commit(tags.filter((_, j) => j !== i))}
            >
              ✕
            </IconButton>
          </span>
        ))}
        <input
          value={draft}
          placeholder={t("site.field.cuisineTypesHint")}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add();
            }
          }}
          onBlur={add}
        />
      </div>
    </div>
  );
}

function StructuredItems({
  form,
  sectionIndex,
  t,
}: {
  form: ReturnType<typeof useForm<SiteFormValues>>;
  sectionIndex: number;
  t: (key: string) => string;
}) {
  const items = useFieldArray({
    control: form.control,
    name: `sections.${sectionIndex}.items` as const,
  });
  return (
    <div>
      {items.fields.map((item, ii) => (
        <div key={item.id} className={actions}>
          <input
            {...form.register(`sections.${sectionIndex}.items.${ii}.name` as const)}
            placeholder={t("site.field.itemName")}
          />
          <input
            {...form.register(`sections.${sectionIndex}.items.${ii}.description` as const)}
            placeholder={t("site.field.itemDescription")}
          />
          <input
            {...form.register(`sections.${sectionIndex}.items.${ii}.price` as const)}
            placeholder={t("site.field.itemPrice")}
            inputMode="decimal"
          />
          <Button type="button" size="xs" variant="ghost" onClick={() => items.remove(ii)}>
            {t("site.menu.removeItem")}
          </Button>
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => items.append({ name: "", description: "", price: "" })}
      >
        {t("site.menu.addItem")}
      </Button>
    </div>
  );
}

function PauseControl({
  onPause,
  label,
  submit,
  disabled,
}: {
  onPause: (iso: string) => void;
  label: string;
  submit: string;
  disabled: boolean;
}) {
  const [value, setValue] = useState("");
  return (
    <span className={actions}>
      <label className={row}>
        {label}
        <input type="datetime-local" value={value} onChange={(e) => setValue(e.target.value)} />
      </label>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled || value === ""}
        onClick={() => onPause(new Date(value).toISOString())}
      >
        {submit}
      </Button>
    </span>
  );
}

const EMPTY_FORM: SiteFormValues = {
  description: "",
  cuisineTypes: "",
  priceRange: 0,
  primaryColor: "",
  logoAssetId: "",
  contactPhone: "",
  contactEmail: "",
  contactWebsite: "",
  menuMode: "NONE",
  menuDocumentIds: "",
  sections: [],
  socialLinks: [],
  galleryOrder: [],
};

/** Map a server 422 field name to the corresponding form field (best-effort; unknown → null). */
function mapField(field: string): keyof SiteFormValues | null {
  if (field === "contact.phone") return "contactPhone";
  if (field === "contact.email") return "contactEmail";
  if (field === "contact.website") return "contactWebsite";
  if (field === "branding.primaryColor") return "primaryColor";
  if (field === "branding.logoAssetId") return "logoAssetId";
  if (field === "priceRange") return "priceRange";
  if (field === "description") return "description";
  return null;
}
